import {
  Header,
  HeaderName,
  HeaderGlobalBar,
  HeaderGlobalAction,
  HeaderPanel,
  HeaderMenuButton,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
  SideNav,
  SideNavItems,
  SideNavLink,
  SideNavDivider,
} from '@carbon/react';
import {
  UserAvatar,
  Help,
  Notification,
  Idea,
  Moon,
  Sun,
  Logout,
  DataStructured,
  Search,
  Document,
  Table,
  ChatBot,
  Home,
} from '@carbon/icons-react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth/auth-context';
import { useNavigate, useLocation } from 'react-router-dom';
import { env } from '@/config/env';
import type { FontScale } from '@/lib/prefs/fontScale';
import { LanguageSwitcher } from './LanguageSwitcher';
import ChangelogModal from './ChangelogModal';
import './AppHeader.scss';

const FONT_SCALE_LABELS: Record<FontScale, string> = {
  normal: 'Normal',
  large: 'Large',
  xlarge: 'Extra large',
};

// Map raw VITE_ENVIRONMENT values to a short, capitalised label.
// Production (or empty/unset) intentionally returns null so no badge is shown.
function getEnvLabel(rawEnv: string): string | null {
  const normalized = rawEnv.trim().toLowerCase();
  if (!normalized || normalized === 'production' || normalized === 'prod') {
    return null;
  }
  if (normalized === 'local') return 'LOCAL';
  if (normalized === 'development' || normalized === 'dev') return 'DEV';
  if (normalized === 'staging' || normalized === 'stg') return 'STAGING';
  return normalized.toUpperCase();
}


type AppHeaderProps = {
  theme: 'g10' | 'g100';
  onToggleTheme: () => void;
  fontScale: FontScale;
  onFontScaleChange: (scale: FontScale) => void;
};

export default function AppHeader({ theme, onToggleTheme, fontScale, onFontScaleChange }: AppHeaderProps) {
  const { t } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserPanelOpen, setIsUserPanelOpen] = useState(false);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  const [isFontScalePanelOpen, setIsFontScalePanelOpen] = useState(false);
  const userPanelRef = useRef<HTMLDivElement>(null);
  const fontScalePanelRef = useRef<HTMLDivElement>(null);

  // Close user panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userPanelRef.current && !userPanelRef.current.contains(event.target as Node)) {
        setIsUserPanelOpen(false);
      }
    }
    if (isUserPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserPanelOpen]);

  // Close font-scale panel when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fontScalePanelRef.current && !fontScalePanelRef.current.contains(event.target as Node)) {
        setIsFontScalePanelOpen(false);
      }
    }
    if (isFontScalePanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFontScalePanelOpen]);

  const handleLogout = () => {
    setIsUserPanelOpen(false);
    logout();
    navigate('/auth');
  };

  const envLabel = getEnvLabel(env.environment);
  const envBadgeModifier = envLabel
    ? `app-header__env-badge--${envLabel.toLowerCase()}`
    : '';

  return (
    <Header aria-label="IBM Science Studio" className="app-header">
      <HeaderMenuButton
        aria-label={t('header.moduleMenu')}
        aria-expanded={isSideNavOpen}
        onClick={() => setIsSideNavOpen(!isSideNavOpen)}
        isActive={isSideNavOpen}
      />

      <HeaderName href="/" prefix="IBM">
        Science Studio
        {envLabel && (
          <span
            className={`app-header__env-badge ${envBadgeModifier}`}
            aria-label={`Environment: ${envLabel}`}
            title={`Environment: ${envLabel}`}
          >
            {envLabel}
          </span>
        )}
      </HeaderName>

      {/* Module name — centred in the header bar */}
      <span className="app-header__module-name" aria-hidden="true">
        Knowledge Management
      </span>

      <HeaderGlobalBar>
        {user && (
          <span className="app-header__user-name">
            {user.display_name || user.email}
          </span>
        )}

        <LanguageSwitcher />

        <HeaderGlobalAction
          aria-label={t('header.fontSize')}
          tooltipAlignment="end"
          isActive={isFontScalePanelOpen}
          onClick={() => {
            setIsFontScalePanelOpen(!isFontScalePanelOpen);
            setIsUserPanelOpen(false);
          }}
          className="app-header__font-scale-action"
        >
          <span aria-hidden="true" style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1, letterSpacing: '-0.5px' }}>Aa</span>
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label={theme === 'g100' ? t('header.themeLight') : t('header.themeDark')}
          onClick={onToggleTheme}
          tooltipAlignment="end"
        >
          {theme === 'g100' ? <Sun size={20} /> : <Moon size={20} />}
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label={t('header.whatsNew')}
          tooltipAlignment="end"
          onClick={() => setIsChangelogOpen(true)}
        >
          <Idea size={20} />
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label={t('header.help')}
          tooltipAlignment="end"
          onClick={() => {}}
        >
          <Help size={20} />
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label={t('header.notifications')}
          tooltipAlignment="end"
          onClick={() => {}}
        >
          <Notification size={20} />
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label={t('header.user')}
          tooltipAlignment="end"
          isActive={isUserPanelOpen}
          onClick={() => {
            setIsUserPanelOpen(!isUserPanelOpen);
          }}
        >
          <UserAvatar size={20} />
        </HeaderGlobalAction>
      </HeaderGlobalBar>

      {/* Module Switcher - Left Side Nav */}
      <SideNav
        aria-label={t('nav.modules')}
        expanded={isSideNavOpen}
        isPersistent={false}
        onOverlayClick={() => setIsSideNavOpen(false)}
        className="app-header__side-nav"
      >
        <SideNavItems>
          <SideNavLink
            renderIcon={Home}
            href="/"
            isActive={location.pathname === '/'}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setIsSideNavOpen(false);
              navigate('/');
            }}
          >
            Home
          </SideNavLink>
          <SideNavDivider />
          <SideNavLink
            renderIcon={Document}
            href="/knowledge-bases"
            isActive={location.pathname.startsWith('/knowledge-bases')}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setIsSideNavOpen(false);
              navigate('/knowledge-bases');
            }}
          >
            {t('nav.knowledgeBase')}
          </SideNavLink>
          <SideNavLink
            renderIcon={DataStructured}
            href="/ontology-builder"
            isActive={
              location.pathname === '/ontology-builder' ||
              location.pathname.startsWith('/editor')
            }
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setIsSideNavOpen(false);
              navigate('/ontology-builder');
            }}
          >
            {t('nav.ontologyBuilder')}
          </SideNavLink>
          <SideNavLink
            renderIcon={Search}
            href="/explorer"
            isActive={
              location.pathname.startsWith('/explorer') ||
              location.pathname.startsWith('/datasets')
            }
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setIsSideNavOpen(false);
              navigate('/explorer');
            }}
          >
            {t('nav.knowledgeExplorer')}
          </SideNavLink>
          <SideNavLink
            renderIcon={Table}
            href="/datasets"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setIsSideNavOpen(false);
              navigate('/datasets');
            }}
          >
            {t('nav.datasets')}
          </SideNavLink>
          <SideNavDivider />
          <SideNavLink
            renderIcon={ChatBot}
            href={env.scientificAgentUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('nav.coScientist')}
          </SideNavLink>
        </SideNavItems>
      </SideNav>

      {/* Font-scale picker panel */}
      <HeaderPanel
        aria-label={t('header.fontSize')}
        expanded={isFontScalePanelOpen}
        ref={fontScalePanelRef}
        className="app-header__font-scale-panel"
      >
        <Switcher aria-label={t('header.fontSize')}>
          {(Object.entries(FONT_SCALE_LABELS) as [FontScale, string][]).map(([value, label]) => (
            <SwitcherItem
              key={value}
              aria-label={label}
              isSelected={fontScale === value}
              onClick={() => {
                onFontScaleChange(value);
                setIsFontScalePanelOpen(false);
              }}
            >
              {label}
            </SwitcherItem>
          ))}
        </Switcher>
      </HeaderPanel>

      {/* User Panel */}
      <HeaderPanel
        aria-label="User panel"
        expanded={isUserPanelOpen}
        ref={userPanelRef}
        className="app-header__user-panel"
      >
        {user && (
          <div className="app-header__user-info">
            <span className="app-header__user-display-name">
              {user.display_name || 'User'}
            </span>
            <span className="app-header__user-email">{user.email}</span>
          </div>
        )}
        <Switcher aria-label="User actions">
          {isAdmin && (
            <SwitcherItem
              aria-label="Admin"
              onClick={() => {
                setIsUserPanelOpen(false);
                navigate('/admin');
              }}
            >
              {t('user.administration')}
            </SwitcherItem>
          )}
          <SwitcherItem
            aria-label="Settings"
            onClick={() => {
              setIsUserPanelOpen(false);
              navigate('/settings');
            }}
          >
            {t('user.settings')}
          </SwitcherItem>
          <SwitcherItem
            aria-label="Usage"
            onClick={() => {
              setIsUserPanelOpen(false);
              navigate('/usage');
            }}
          >
            {t('user.usage')}
          </SwitcherItem>
          <SwitcherDivider />
          <SwitcherItem aria-label={t('user.logout')} onClick={handleLogout}>
            <Logout size={16} style={{ marginRight: '0.5rem' }} />
            {t('user.logout')}
          </SwitcherItem>
        </Switcher>
      </HeaderPanel>

      <ChangelogModal open={isChangelogOpen} onClose={() => setIsChangelogOpen(false)} />
    </Header>
  );
}
